import React, { useEffect, useState } from 'react';
import api from '../api';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const COLORS = ['#4caf50', '#ff9800', '#9e9e9e', '#2196f3', '#9c27b0', '#f44336', '#03a9f4', '#ffc107', '#795548', '#607d8b', '#8bc34a', '#e91e63'];

export default function AdminDashboard() {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [distribution, setDistribution] = useState([]);
    const [stateDistribution, setStateDistribution] = useState([]);
    const [signups, setSignups] = useState([]);
    const [completions, setCompletions] = useState([]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [sRes, distRes, suRes, cRes, stRes] = await Promise.all([
                    api.get('/admin/api/schools/dashboard/summary'),
                    api.get('/admin/api/schools/dashboard/status-distribution'),
                    api.get('/admin/api/schools/dashboard/signups?days=30'),
                    api.get('/admin/api/schools/dashboard/completions?days=30'),
                    api.get('/admin/api/schools/dashboard/state-distribution'),
                ]);

                if (sRes.status === 403) throw new Error('forbidden');

                const sJson = sRes.data;
                const distJson = distRes.data ?? null;
                const suJson = suRes.data ?? [];
                const cJson = cRes.data ?? [];
                const stJson = stRes.data ?? [];

                setSummary(sJson);

                setDistribution([
                    { name: 'Completed', value: sJson?.completedCount || 0 },
                    { name: 'Draft', value: sJson?.draftCount || 0 },
                    { name: 'Not Started', value: sJson?.notStartedCount || 0 },
                ]);

                setStateDistribution((stJson || []).map(r => ({ name: r.state || 'Unknown', value: Number(r.cnt) })));

                setSignups((suJson || []).map(r => ({ date: r.day, count: Number(r.cnt) })));
                setCompletions((cJson || []).map(r => ({ date: r.day, count: Number(r.cnt) })));

            } catch (err) {
                if (err.message === 'forbidden') {
                    console.error('Dashboard API returned 403 — check authentication token.');
                } else {
                    console.error(err);
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 3 }}>
            {/* Left-aligned content wrapper so content sits after the sidebar */}
            <Box sx={{ width: { xs: '100%', md: '1200px' }, ml: 0 }}>
                <Typography variant="h4" sx={{ mb: 3, textAlign: 'left' }}>Admin Dashboard</Typography>

                {/* Top cards row (flex, equal widths) */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ flex: '1 1 25%', maxWidth: { xs: '100%', md: '25%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>Average completion time</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{summary?.averageCompletionSeconds ? `${Math.round(summary.averageCompletionSeconds / 3600)} hrs` : '—'}</Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 25%', maxWidth: { xs: '100%', md: '25%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>Completed</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{summary?.completedCount ?? 0}</Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 25%', maxWidth: { xs: '100%', md: '25%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>Draft</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{summary?.draftCount ?? 0}</Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 25%', maxWidth: { xs: '100%', md: '25%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>Not Started</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{summary?.notStartedCount ?? 0}</Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {/* Signups - full width (30 days) */}
                <Box sx={{ mb: 2 }}>
                    <Card sx={{ width: '100%' }}>
                        <CardContent sx={{ px: 0 }}>
                            <Box sx={{ px: 2, pt: 1 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1 }}>Signups (last 30 days)</Typography>
                            </Box>
                            <Box sx={{ width: '100%', height: { xs: 260, md: 320 } }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={signups} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                        <XAxis dataKey="date" tickMargin={18} height={60} />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ marginTop: 16 }} />
                                        <Line type="monotone" dataKey="count" stroke="#1976d2" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Completions - full width (30 days), same size as Signups */}
                <Box sx={{ mb: 2 }}>
                    <Card sx={{ width: '100%' }}>
                        <CardContent sx={{ px: 0 }}>
                            <Box sx={{ px: 2, pt: 1 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1 }}>Completions (last 30 days)</Typography>
                            </Box>
                            <Box sx={{ width: '100%', height: { xs: 260, md: 320 } }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={completions} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                        <XAxis dataKey="date" tickMargin={18} height={60} />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ marginTop: 16 }} />
                                        <Line type="monotone" dataKey="count" stroke="#4caf50" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Two separate cards: Status Distribution and State Distribution */}
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>Status Distribution</Typography>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{ width: { xs: '80%', md: '560px' }, aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="75%" labelLine={false} label>
                                                    {distribution.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Legend verticalAlign="bottom" height={120} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>State Distribution</Typography>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{ width: { xs: '90%', md: '560px' }, aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={stateDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="75%" labelLine={false} label>
                                                    {stateDistribution.map((entry, index) => (
                                                        <Cell key={`cell-state-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Legend verticalAlign="bottom" height={120} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
